// Authoritative, idempotent guest → account hand-off.
//
// `claim_guest_bookings` is the single DB owner of the transfer. It moves
// bookings, quotes, shipment requests and conversations that belong to a
// guest shell (same e-mail, never signed in) onto the authenticated
// account. It is safe to call repeatedly: a second call transfers zero
// rows and returns the same shape.
//
// Failures are NEVER swallowed. The caller receives the error and the
// attempt is remembered so it can be retried on the next auth event.
import { supabase } from "@/integrations/supabase/client";

export interface GuestClaimResult {
  ok: true;
  shells: number;
  bookings: number;
  quotes: number;
  shipment_requests: number;
  conversations: number;
}

export class GuestClaimError extends Error {
  constructor(message: string, readonly cause?: unknown) {
    super(message);
    this.name = "GuestClaimError";
  }
}

const PENDING_KEY = "ec_guest_claim_pending";

/** Marks a user id as needing a (re)try of the hand-off. */
export function markGuestClaimPending(userId: string): void {
  try { localStorage.setItem(PENDING_KEY, userId); } catch { /* storage blocked */ }
}

export function clearGuestClaimPending(): void {
  try { localStorage.removeItem(PENDING_KEY); } catch { /* storage blocked */ }
}

export function isGuestClaimPending(userId: string): boolean {
  try { return localStorage.getItem(PENDING_KEY) === userId; } catch { return false; }
}

/**
 * Runs the hand-off. Throws `GuestClaimError` on failure (and leaves the
 * pending marker in place so the next sign-in retries).
 */
export async function claimGuestRecords(
  userId: string,
  email: string,
): Promise<GuestClaimResult> {
  if (!userId || !email) throw new GuestClaimError("missing_identity");
  markGuestClaimPending(userId);

  const { data, error } = await supabase.rpc("claim_guest_bookings", {
    p_user_id: userId,
    p_email: email,
  });

  if (error) {
    throw new GuestClaimError(error.message || "claim_failed", error);
  }

  clearGuestClaimPending();
  const raw = (data ?? {}) as Partial<GuestClaimResult>;
  return {
    ok: true,
    shells: Number(raw.shells ?? 0),
    bookings: Number(raw.bookings ?? 0),
    quotes: Number(raw.quotes ?? 0),
    shipment_requests: Number(raw.shipment_requests ?? 0),
    conversations: Number(raw.conversations ?? 0),
  };
}
